import { useState } from 'react';
import {
  medPassRecord,
  medPassScan,
  type ApiClient,
  type ApiError,
  type HoldVital,
  type MedPassNeeds,
  type MedPassRow,
  type MedScanResult,
} from '@aithora/shared';
import { ErrorNote } from '../../../ErrorNote';
import { A, sx } from '../parts';
import { PocDialog } from './PocDialog';
import { ALERT, CARD, CODE, NOTE, NUM, STEP, chip, hhmm, prim } from './styles';

/** The readings a hold rule can ask for, as the design's chart names them. */
export const VITAL: Record<HoldVital, { label: string; unit: string }> = {
  pulse: { label: 'Pulse', unit: 'bpm' },
  sbp: { label: 'BP top number', unit: 'mmHg' },
  dbp: { label: 'BP bottom number', unit: 'mmHg' },
  temp: { label: 'Temperature', unit: '°F' },
  spo2: { label: 'Oxygen', unit: '%' },
};

/** The readings typed so far, as numbers (blank and unreadable ones left out). */
export function vitalsOf(v: Partial<Record<HoldVital, string>>): Partial<Record<HoldVital, number>> {
  const out: Partial<Record<HoldVital, number>> = {};
  for (const k of Object.keys(v) as HoldVital[]) {
    const n = parseFloat(String(v[k] ?? '').trim());
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}

/** How far ahead a dose counts as due on this pass. */
export const PASS_AHEAD_MS = 60 * 60_000;

/**
 * The doses on the pass now: due within an hour either side of now (or recorded here), earliest
 * first. When nothing is due now, the next pass: every dose due in the same hour as the next one.
 */
export function passDoses<T extends { dueAt?: string | null }>(open: T[], now: number, recorded: (m: T) => boolean = () => false): T[] {
  const at = (m: T) => (m.dueAt ? Date.parse(m.dueAt) : NaN);
  const timed = open.filter((m) => Number.isFinite(at(m)) || recorded(m)).sort((x, y) => (at(x) || 0) - (at(y) || 0));
  // The pass window (design: "9 PM pass, within the 8 to 10 PM window"): an hour either side.
  // A dose missed on an earlier pass is the med pass list's late row, not this scan.
  const now1 = timed.filter((m) => recorded(m) || (at(m) <= now + PASS_AHEAD_MS && at(m) >= now - PASS_AHEAD_MS));
  if (now1.length) return now1;
  const first = timed.find((m) => Number.isFinite(at(m)) && at(m) >= now - PASS_AHEAD_MS);
  if (!first) return [];
  const hour = Math.floor(at(first) / 3_600_000);
  return timed.filter((m) => Math.floor(at(m) / 3_600_000) === hour);
}

const NOTE_TEXT = 'The console has no camera. Type or paste what your scanner reads. The phone app scans with its camera.';

type MedState = { scan?: MedScanResult; done?: { status: 'GIVEN' | 'HELD'; at: number }; error?: ApiError; busy?: boolean };

/**
 * Med pass by scanning (design P.vm scan): the wristband, then each package, checked
 * against the order by POST /med-pass/scan (the five rights and the hold rules). A pass is
 * recorded Given with its scanId, a hold is recorded Held with it, and a block records
 * nothing and says why in the server's words.
 */
export function ScanSheet({ client, meds, onClose, onRecorded, onNeeds }: {
  client: ApiClient;
  /** Today's med pass rows. */
  meds: MedPassRow[];
  onClose: () => void;
  /** A dose was recorded: reload the med pass. */
  onRecorded: () => void;
  /** POST /med-pass wants more for this order (a count, a witness...): the med pass sheet takes it from here, with the scan. */
  onNeeds: (med: MedPassRow, needs: MedPassNeeds, scanId: string, timingReason: string | null) => void;
}) {
  const [picked, setWho] = useState('');
  const [band, setBand] = useState('');
  const [pkg, setPkg] = useState<Record<string, string>>({});
  const [vitals, setVitals] = useState<Partial<Record<HoldVital, string>>>({});
  const [timing, setTiming] = useState<Record<string, string>>({});
  const [state, setState] = useState<Record<string, MedState>>({});
  const [last, setLast] = useState<MedScanResult | null>(null);
  const put = (id: string, s: MedState) => setState((p) => ({ ...p, [id]: s }));
  // The doses still to give, and the ones recorded here (kept on screen after the reload).
  const open = meds.filter((m) => !m.today_status || state[m.medication_id]?.done);
  // This pass only (design: the residents due now, not the whole building): doses due within
  // an hour either side of now, plus any recorded here. With none due now, the next pass.
  const pass = passDoses(open, Date.now(), (m) => !!state[m.medication_id]?.done);
  const people = [...new Map(pass.map((m) => [m.resident_id, m])).values()];
  // The first resident due is picked, so step 2 is on screen; the wristband still has to scan.
  const who = picked || people[0]?.resident_id || '';

  const mine = pass.filter((m) => m.resident_id === who);
  const person = people.find((m) => m.resident_id === who) ?? null;
  const firstDue = mine.map((m) => m.dueAt).filter((d): d is string => !!d).sort()[0] ?? null;
  // The readings a hold rule asked for, on any of this resident's medicines.
  const needed = [...new Set(mine.flatMap((m) => state[m.medication_id]?.scan?.needs ?? []))];
  const neededFor = (k: HoldVital) =>
    mine.filter((m) => state[m.medication_id]?.scan?.needs.includes(k)).map((m) => state[m.medication_id]!.scan!.order.name).join(' and ') || 'this medicine';
  const wrong = mine.map((m) => state[m.medication_id]?.scan).find((s) => s?.outcome === 'blocked') ?? null;

  const record = async (m: MedPassRow, status: 'GIVEN' | 'HELD', scan: MedScanResult, tr: string | null) => {
    try {
      await medPassRecord(client, {
        body: {
          medicationId: m.medication_id,
          residentId: m.resident_id,
          status,
          medicationName: m.name,
          scanId: scan.scanId,
          ...(status === 'HELD' ? { reason: scan.recordAs?.reason ?? 'held_per_order' } : {}),
          ...(tr ? { timingReason: tr } : {}),
        },
      });
      put(m.medication_id, { scan, done: { status, at: Date.now() } });
      onRecorded();
    } catch (e) {
      const err = e as ApiError;
      const body = err.body as MedPassNeeds | undefined;
      if (err.status === 422 && Array.isArray(body?.needs) && !body!.needs.includes('scan')) {
        onNeeds(m, body!, scan.scanId, tr);
        return;
      }
      put(m.medication_id, { scan, error: err });
    }
  };

  const scanAndGive = async (m: MedPassRow) => {
    const s = state[m.medication_id] ?? {};
    if (s.busy || s.done) return;
    // A hold already found: this tap records it Held, with the scan.
    if (s.scan?.outcome === 'hold') {
      put(m.medication_id, { ...s, busy: true });
      await record(m, 'HELD', s.scan, timing[m.medication_id]?.trim() || null);
      return;
    }
    put(m.medication_id, { busy: true });
    const tr = timing[m.medication_id]?.trim() || null;
    try {
      const v = vitalsOf(vitals);
      const r = await medPassScan(client, {
        body: {
          medicationId: m.medication_id,
          wristbandCode: band.trim(),
          packageCode: (pkg[m.medication_id] ?? '').trim(),
          ...(Object.keys(v).length ? { vitals: v } : {}),
          ...(tr ? { timingReason: tr } : {}),
        },
      });
      setLast(r);
      if (r.outcome === 'pass') {
        put(m.medication_id, { scan: r, busy: true });
        await record(m, 'GIVEN', r, tr);
        return;
      }
      put(m.medication_id, { scan: r });
    } catch (e) {
      put(m.medication_id, { error: e as ApiError });
    }
  };

  const stateText = (m: MedPassRow): { text: string; color: string } => {
    const s = state[m.medication_id] ?? {};
    if (s.done) return { text: s.done.status === 'GIVEN' ? `Given · ${hhmm(s.done.at)}` : `Held · ${hhmm(s.done.at)}`, color: '#1F6B52' };
    const r = s.scan;
    if (r?.outcome === 'hold') return { text: r.reason ?? 'Hold this dose and tell the nurse.', color: '#B3261E' };
    if (r?.outcome === 'needs') return { text: r.reason ?? 'Take the reading first.', color: '#B3261E' };
    if (r?.outcome === 'blocked') return { text: 'Blocked. Nothing was recorded.', color: '#B3261E' };
    return { text: 'Ready', color: '#3C454F' };
  };

  return (
    <PocDialog title="Med pass · scan" onClose={onClose} testId="poc-scan">
      <div style={sx(STEP)}>1 · Scan the wristband</div>
      {people.length === 0 && <span style={sx('font-size:16px;color:#3C454F;')}>Every dose on today’s med pass is recorded.</span>}
      <div style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
        {people.map((p) => (
          <A
            key={p.resident_id}
            style={chip(who === p.resident_id)}
            onClick={() => {
              setWho(p.resident_id);
              setBand('');
              setVitals({});
              setLast(null);
            }}
          >
            {`Scan wristband · ${p.resident_name ?? 'Resident'}`}
          </A>
        ))}
      </div>
      {person && (
        <>
          <input
            type="text"
            value={band}
            onChange={(e) => setBand(e.target.value)}
            placeholder="Type or paste the scanned code"
            aria-label="Wristband code"
            autoComplete="off"
            style={sx(CODE)}
          />
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;margin-top:4px;')}>{[person.resident_name ?? 'Resident', firstDue ? hhmm(firstDue) : null].filter(Boolean).join(' · ')}</span>
          {needed.map((k) => (
            <div key={k} style={sx('display:flex;align-items:center;gap:10px;')}>
              <span style={sx('flex:1 1 auto;font-size:16px;font-weight:700;')}>{`${VITAL[k].label} before ${neededFor(k)}`}</span>
              <input
                type="text"
                inputMode="decimal"
                value={vitals[k] ?? ''}
                onChange={(e) => setVitals((p) => ({ ...p, [k]: e.target.value }))}
                placeholder="–"
                aria-label={`${VITAL[k].label} in ${VITAL[k].unit}`}
                style={sx(`flex:0 0 84px;width:84px;${NUM}text-align:right;`)}
              />
              <span style={sx('font-size:16px;color:#54606C;')}>{VITAL[k].unit}</span>
            </div>
          ))}
          <div style={sx(STEP)}>2 · Scan each medicine</div>
          {mine.map((m) => {
            const s = state[m.medication_id] ?? {};
            const st = stateText(m);
            const hold = s.scan?.outcome === 'hold' && !s.done;
            const timeBlocked = s.scan?.outcome === 'blocked' && s.scan.failed === 'time';
            const canGo = !s.done && !s.busy && (hold || (!!band.trim() && !!(pkg[m.medication_id] ?? '').trim()));
            const name = [m.name, m.dosage].filter(Boolean).join(' ') || 'Medicine';
            // The order's route once a scan has read it ("oral" is "by mouth"), else how often it is given.
            const r0 = s.scan?.order.route ?? null;
            const route = r0 ? (r0 === 'oral' ? 'by mouth' : r0) : m.frequency;
            return (
              <div key={m.medication_id} data-testid={`scan-${m.medication_id}`} style={sx(CARD)}>
                <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{name}</span>
                {route && <span style={sx('font-size:16px;color:#54606C;')}>{route}</span>}
                <span style={sx(`font-size:16px;font-weight:700;color:${st.color};`)}>{st.text}</span>
                {!s.done && !hold && (
                  <input
                    type="text"
                    value={pkg[m.medication_id] ?? ''}
                    onChange={(e) => setPkg((p) => ({ ...p, [m.medication_id]: e.target.value }))}
                    placeholder="Type or paste the scanned code"
                    aria-label={`Package code for ${name}`}
                    autoComplete="off"
                    style={sx(CODE)}
                  />
                )}
                {timeBlocked && (
                  <input
                    type="text"
                    value={timing[m.medication_id] ?? ''}
                    onChange={(e) => setTiming((p) => ({ ...p, [m.medication_id]: e.target.value }))}
                    placeholder="Why it is late or early"
                    aria-label={`Why ${name} is late or early`}
                    style={sx(CODE)}
                  />
                )}
                <A style={prim(canGo, hold)} onClick={() => void scanAndGive(m)} disabled={!canGo}>
                  {s.done ? 'Done' : hold ? 'Mark held, tell the nurse' : 'Scan and give'}
                </A>
                {s.error && <ErrorNote error={s.error} compact />}
              </div>
            );
          })}
          {wrong?.reason && (
            <div role="alert" style={sx(ALERT)}>
              {wrong.reason}
            </div>
          )}
          {last && last.resident.id === who && (
            <>
              <div style={sx(STEP)}>Five rights, checked on every scan</div>
              <div style={sx(CARD)}>
                {last.rights.map((r) => (
                  <div key={r.key} style={sx('display:flex;gap:8px;align-items:baseline;')}>
                    <span style={sx(`color:${r.ok ? '#1F6B52' : '#B3261E'};font-weight:800;`)}>{r.ok ? '✓' : '✗'}</span>
                    <span style={sx('font-size:16px;color:#22272E;')}>
                      <b>{r.label}</b>
                      {` · ${r.text}`}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      <span style={sx(NOTE)}>{NOTE_TEXT}</span>
    </PocDialog>
  );
}
